import { observer } from "mobx-react"
import { DATE_FORMAT } from "modules/common/constants"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import moment from "moment"
import React, { useEffect, useRef } from "react"
import { Draggable } from "@hello-pangea/dnd"
import { QuestionCardType } from "../models/card"
import styles from "./QuestionCard.module.scss"

/** Инициалы для аватарки участника: «Иванов И.И.» → «ИИ» */
const initials = (name: string) => {
  const parts = name.split(" ").filter((p) => !!p)

  if (parts.length === 0) {
    return "?"
  }

  const first = parts[0][0] || ""
  const second = parts.length > 1 ? parts[1][0] : ""

  return `${first}${second}`.toUpperCase()
}

/** Цвет метки сотрудника стабилен: одинаковый на всей доске */
const AVATAR_COLORS = ["#36acd7", "#1dd278", "#7d36d7", "#ea561e", "#eab308", "#00273d"]

const avatarColor = (id: string) => {
  let hash = 0

  for (let i = 0; i < id.length; i++) {
    hash = (hash + id.charCodeAt(i)) % AVATAR_COLORS.length
  }

  return AVATAR_COLORS[hash]
}

const Person: React.FC<{ id: string; name: string; role: string; own?: boolean }> = ({
  id,
  name,
  role,
  own,
}) => (
  <div
    className={`${styles.avatar} ${own ? styles.own : ""}`}
    style={{ backgroundColor: avatarColor(id) }}
    title={`${role}: ${name}${own ? " — это вы" : ""}`}
  >
    {initials(name)}
  </div>
)

const QuestionCardView: React.FC<QuestionCardProps> = ({
  question,
  index,
  color,
  archive,
  tagColors,
  hideProject,
  currentUserId,
  revealed,
  compact,
  onOpen,
  onMenu,
}) => {
  const node = useRef<HTMLDivElement | null>(null)

  // карточку из ссылки показываем сами: колонка могла прокрутиться далеко от неё
  useEffect(() => {
    revealed && node.current && node.current.scrollIntoView({ block: "center", inline: "nearest" })
  }, [revealed])

  const stopDate = question.stopDate ? moment(question.stopDate, DATE_FORMAT) : null
  const today = moment().startOf("day")
  // в архивной колонке задание уже отработано: срок в ней ни о чём не сигналит
  const tracked = !question.completed && !archive
  const expired = tracked && !!stopDate && stopDate.isBefore(today)
  const soon = tracked && !!stopDate && !expired && stopDate.diff(today, "days") <= 2

  // исполнитель на карточке важнее ответственного, поэтому он первым
  const people = [
    ...question.implementers.map((person) => ({ ...person, role: "Исполнитель" })),
    ...question.employers.map((person) => ({ ...person, role: "Ответственный" })),
  ].filter((person, i, all) => all.findIndex((p) => p.id === person.id) === i)

  return (
    <Draggable draggableId={question.id} index={index} isDragDisabled={!question.canMoveQuestion}>
      {(provided, snapshot) => (
        <div
          ref={(el: HTMLDivElement | null) => {
            node.current = el
            provided.innerRef(el)
          }}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`${styles.card} ${snapshot.isDragging ? styles.dragging : ""} ${
            revealed ? styles.revealed : ""
          } ${compact ? styles.compact : ""}`}
          onClick={() => onOpen(question)}
          // правым кликом открывается меню действий: своё меню браузера здесь не нужно
          onContextMenu={(e: React.MouseEvent) => {
            if (!onMenu) {
              return
            }

            e.preventDefault()
            onMenu(question, e.clientX, e.clientY)
          }}
        >
          <span className={styles.stripe} style={{ backgroundColor: color }} />

          <div className={styles.head}>
            <div className={styles.title}>{question.title || `Задание ${question.number}`}</div>
            <div className={styles.number}>{question.number}</div>
          </div>

          {!hideProject && question.order && (
            <div className={styles.project} title={question.order.name}>
              {question.order.inventoryNumber} {question.order.name}
            </div>
          )}

          <div className={styles.footer}>
            {/* срок необязателен: у карточки личной доски его может не быть */}
            {!!question.stopDay && (
              <div
                className={`${styles.term} ${expired ? styles.expired : ""} ${soon ? styles.soon : ""}`}
                title={expired ? "Срок вышел" : "Срок исполнения"}
              >
                <GeneralIcon type="general-clock" size={12} />
                <span>{question.stopDay}</span>
              </div>
            )}

            <div className={styles.marks}>
              {question.filesCount > 0 && (
                <span className={styles.mark} title={`Файлов: ${question.filesCount}`}>
                  <GeneralIcon type="general-attach" size={12} />
                  {question.filesCount}
                </span>
              )}
              {question.messagesCount > 0 && (
                <span className={styles.mark} title={`Комментариев: ${question.messagesCount}`}>
                  <GeneralIcon type="general-msg" size={12} />
                  {question.messagesCount}
                </span>
              )}
              {/* единственный исполнитель — он сам: счётчик «1/1» ему ничего не говорит,
                  показываем словами. Когда исполнителей несколько, счётчик остаётся */}
              {question.executorsCount > 0 && !(question.ownReady && question.executorsCount === 1) && (
                <span
                  className={`${styles.mark} ${
                    question.readyCount >= question.executorsCount ? styles.markReady : ""
                  }`}
                  title={`Готовность исполнителей: ${question.readyCount} из ${question.executorsCount}`}
                >
                  <GeneralIcon type="general-check" size={12} />
                  {question.readyCount}/{question.executorsCount}
                </span>
              )}
              {question.childrenCount > 0 && (
                <span className={styles.mark} title={`Подзадач: ${question.childrenCount}`}>
                  <GeneralIcon type="general-list" size={12} />
                  {question.childrenCount}
                </span>
              )}
            </div>

            <div className={styles.people}>
              {people.slice(0, 2).map((person) => (
                <Person
                  key={person.id}
                  id={person.id}
                  name={person.name}
                  role={person.role}
                  own={person.id === currentUserId}
                />
              ))}
              {people.length > 2 && (
                <div
                  className={styles.more}
                  title={people
                    .slice(2)
                    .map((person) => `${person.role}: ${person.name}`)
                    .join("\n")}
                >
                  +{people.length - 2}
                </div>
              )}
            </div>
          </div>

          {/* отдельной строкой: в строке с иконками и людьми надпись уже не помещается */}
          {question.ownReady && (
            <div className={styles.ownReady} title="Вы отметили выполнение">
              Вы выполнили
            </div>
          )}

          {question.tags.length > 0 && (
            <div className={styles.tags}>
              {question.tags.map((tag) => (
                <span className={styles.tag} key={tag} title={tag} style={{ color: tagColors[tag] }}>
                  <span className={styles.tagDot} style={{ backgroundColor: tagColors[tag] }} />
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </Draggable>
  )
}

/**
 * Карточек в колонке много, а меняется за раз одна: observer мемоизирует компонент
 * и перерисовывает только ту карточку, чьи поля изменились
 */
export const QuestionCard = observer(QuestionCardView)

interface QuestionCardProps {
  question: QuestionCardType
  index: number
  /** Цвет колонки: им красится полоска слева у своих заданий */
  color: string
  /** Колонка архивная: срок задания в ней не подсвечивается */
  archive?: boolean
  /** Цвета меток из справочника: название → цвет текста */
  tagColors: TStringMap<string>
  hideProject?: boolean
  currentUserId: string
  /** Карточка из ссылки: подсвечиваем её и прокручиваем к ней колонку */
  revealed?: boolean
  /** Узкая доска виджета: карточка плотнее, второстепенное на ней не помещается */
  compact?: boolean
  onOpen: (question: { id: string }) => void
  /** Меню действий по правой кнопке: доска знает права и открывает нужные диалоги */
  onMenu?: (question: QuestionCardType, x: number, y: number) => void
}
